import {useRef} from 'react';
import Draggable from 'react-draggable';
import styled from 'styled-components/macro';
import theme from 'styled-theming';
import {useNotifications} from '@contexts/notificationsContext';
import {dark, light, colors, textSizes} from '@styles/vars';

const NotificationContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9999;
  pointer-events: none;
`;

const NotificationWindow = styled.div`
  pointer-events: auto;
  width: 300px;
  min-height: ${props => props.minimized ? '40px' : 'auto'};
  background: ${theme('theme', {
    light: light.widget,
    dark: dark.widget
  })};
  border: 1px solid ${theme('theme', {
    light: light.border,
    dark: dark.border
  })};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  margin-bottom: 10px;
  overflow: hidden;
  transition: all 0.2s ease;
`;

const NotificationHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: ${theme('theme', {
    light: light.highlight,
    dark: dark.highlight
  })};
  border-bottom: 1px solid ${theme('theme', {
    light: light.border,
    dark: dark.border
  })};
`;

const NotificationDragHandle = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: grab;
  user-select: none;

  &:active {
    cursor: grabbing;
  }

  .notification-drag-hint {
    flex-shrink: 0;
    font-size: 16px;
    line-height: 1;
    opacity: 0.55;
    color: ${colors.textSecondary};
  }

  &:hover .notification-drag-hint {
    opacity: 0.9;
  }
`;

const NotificationTitle = styled.div`
  font-weight: 600;
  font-size: ${textSizes['14']};
  color: ${props => {
    switch (props.type) {
      case 'success': return colors.success;
      case 'error': return colors.error;
      case 'warning': return colors.warning;
      default: return colors.info;
    }
  }};
`;

const NotificationActions = styled.div`
  display: flex;
  gap: 4px;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  color: ${colors.textSecondary};
  font-size: 12px;
  transition: background-color 0.2s;

  &:hover {
    background: ${theme('theme', {
      light: light.border,
      dark: dark.border
    })};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${colors.blue};
  }
`;

const NotificationContent = styled.div`
  padding: ${props => props.minimized ? '0' : '12px'};
  font-size: ${textSizes['14']};
  color: ${colors.text};
  line-height: 1.4;
`;

const DraggableNotification = ({notification, onDragStop, onMinimize, onClose}) => {
  const nodeRef = useRef(null);

  return (
    <Draggable
      nodeRef={nodeRef}
      defaultPosition={notification.position}
      onStop={(e, data) => {
        if (!data || typeof data.x !== 'number' || typeof data.y !== 'number') {
          return;
        }
        onDragStop(notification.id, e, data);
      }}
      handle=".notification-drag-handle"
    >
      <div ref={nodeRef}>
        <NotificationWindow minimized={notification.minimized}>
          <NotificationHeader>
            <NotificationDragHandle className="notification-drag-handle" title="Arrastrar">
              <i className="icon icon-dots notification-drag-hint" aria-hidden="true"/>
              <NotificationTitle type={notification.type}>
                {notification.type === 'success' && '✓ Éxito'}
                {notification.type === 'error' && '✕ Error'}
                {notification.type === 'warning' && '⚠ Advertencia'}
                {notification.type === 'info' && 'ℹ Información'}
              </NotificationTitle>
            </NotificationDragHandle>
            <NotificationActions>
              <ActionButton type="button" onClick={(e) => onMinimize(notification.id, e)}>
                {notification.minimized ? '□' : '−'}
              </ActionButton>
              <ActionButton type="button" onClick={(e) => onClose(notification.id, e)}>
                ✕
              </ActionButton>
            </NotificationActions>
          </NotificationHeader>
          {!notification.minimized && (
            <NotificationContent>
              {notification.message}
            </NotificationContent>
          )}
        </NotificationWindow>
      </div>
    </Draggable>
  );
};

const NotificationManager = () => {
  const {notifications, removeNotification, minimizeNotification, updatePosition} = useNotifications();

  const handleMinimize = (id, e) => {
    e.stopPropagation();
    minimizeNotification(id);
  };

  const handleClose = (id, e) => {
    e.stopPropagation();
    removeNotification(id);
  };

  const handleDragStop = (id, e, data) => {
    updatePosition(id, {x: data.x, y: data.y});
  };

  return (
    <NotificationContainer>
      {notifications.map(notification => (
        <DraggableNotification
          key={notification.id}
          notification={notification}
          onDragStop={handleDragStop}
          onMinimize={handleMinimize}
          onClose={handleClose}
        />
      ))}
    </NotificationContainer>
  );
};

export default NotificationManager;